import React, { useState, useEffect } from 'react';
import { X, Loader, Receipt, ChevronRight, ChevronLeft, Check, AlertCircle, CheckCircle2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { financialTransactionAPI } from './financialTransactionService';
import { btn } from '../utils/buttonStyles';

const STEPS = [
  { number: 1, label: 'Details' },
  { number: 2, label: 'Who Paid' },
  { number: 3, label: 'Split With' },
];

const SEASONS = ['Year', 'Spring', 'Summer', 'Fall', 'Winter', 'Q1', 'Q2', 'Q3', 'Q4', 'H1', 'H2'];
const CURRENT_YEAR = new Date().getFullYear();

const getMemberName = m =>
  `${m.firstName ?? m.first_name ?? ''} ${m.lastName ?? m.last_name ?? ''}`.trim() ||
  m.username || 'Member';

const getInitials = m => {
  const first = (m.firstName ?? m.first_name ?? '').charAt(0);
  const last  = (m.lastName  ?? m.last_name  ?? '').charAt(0);
  return (first + last).toUpperCase() || '?';
};

const AVATAR_COLORS = [
  'bg-blue-500', 'bg-violet-500', 'bg-emerald-500',
  'bg-orange-500', 'bg-pink-500', 'bg-teal-500',
];
const avatarColor = (id, idx) => AVATAR_COLORS[idx % AVATAR_COLORS.length];

const AddExpenseComponent = ({ theme, groupId, members, currentUserId, onClose, onSaved, expense }) => {
  const allMembers = members || [];
  const isEditMode = !!expense;

  // Derive payer and participants from existing expense when editing
  const initPayerId = isEditMode
    ? String(expense.creditTransactions?.[0]?.toUserId ?? currentUserId)
    : String(currentUserId);

  const initParticipantIds = isEditMode
    ? new Set([
        initPayerId,
        ...expense.creditTransactions.map(ct => String(ct.userId)),
      ])
    : new Set(allMembers.map(m => String(m.userId)));

  const [step, setStep]             = useState(1);
  const [title, setTitle]           = useState(isEditMode ? expense.title : '');
  const [amount, setAmount]         = useState(isEditMode ? String(Math.round(expense.totalAmount)) : '');
  const [dueDate, setDueDate]       = useState('');
  const [season, setSeason]         = useState(isEditMode ? expense.season : 'Year');
  const [seasonYear, setSeasonYear] = useState(isEditMode ? expense.seasonYear : CURRENT_YEAR);
  const [payerId, setPayerId]       = useState(initPayerId);
  const [saving, setSaving]         = useState(false);

  // Step 3 — participants (includes payer)
  const participantPool = allMembers;
  const [participantIds, setParticipantIds] = useState(() => initParticipantIds);

  // Split mode: 'equal' | 'custom' — detect from existing expense
  const initSplitMode = (() => {
    if (!isEditMode || !expense.creditTransactions?.length) return 'equal';
    const amounts = expense.creditTransactions.map(ct => Number(ct.amount));
    const allEqual = amounts.every(a => a === amounts[0]);
    return allEqual ? 'equal' : 'custom';
  })();
  const [splitMode, setSplitMode] = useState(initSplitMode);

  // Custom amounts: { [userId]: string }
  const initCustomAmounts = isEditMode
    ? Object.fromEntries(expense.creditTransactions.map(ct => [String(ct.userId), String(Math.round(ct.amount))]))
    : {};
  const [customAmounts, setCustomAmounts] = useState(initCustomAmounts);

  const parsedAmount     = parseInt(amount) || 0;
  const participantCount = participantIds.size;

  // Equal split per person
  const equalPerPerson = participantCount > 0 && parsedAmount > 0
    ? Math.floor(parsedAmount / participantCount)
    : 0;

  // Total allocated in custom mode (only selected participants)
  const customTotal = participantPool
    .filter(m => participantIds.has(String(m.userId)))
    .reduce((sum, m) => sum + (parseInt(customAmounts[String(m.userId)]) || 0), 0);

  const remaining   = parsedAmount - customTotal;
  const isBalanced  = remaining === 0;

  // When switching to custom mode, pre-fill equal amounts
  useEffect(() => {
    if (splitMode === 'custom' && parsedAmount > 0 && participantCount > 0) {
      const perHead = Math.floor(parsedAmount / participantCount);
      const remainder = parsedAmount - perHead * participantCount;
      const ids = [...participantIds];
      const next = {};
      ids.forEach((id, i) => {
        // Give remainder to first person
        next[id] = String(perHead + (i === 0 ? remainder : 0));
      });
      setCustomAmounts(next);
    }
  }, [splitMode]); // eslint-disable-line react-hooks/exhaustive-deps

  // Validation
  const step1Valid = title.trim() && parsedAmount > 0;
  const step2Valid = !!payerId;
  const step3Valid = participantCount > 0 && (splitMode === 'equal' || isBalanced);

  const handlePayerSelect = id => {
    setPayerId(id);
    setParticipantIds(new Set(allMembers.map(m => String(m.userId))));
  };

  const toggleParticipant = id => {
    setParticipantIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const handleNext = () => setStep(s => s + 1);
  const handleBack = () => setStep(s => s - 1);

  const handleSubmit = async () => {
    if (!step3Valid) return;
    if (!payerId || payerId === 'undefined') {
      toast.error('Payer has no valid user ID');
      return;
    }
    setSaving(true);
    try {
      const selectedMembers = allMembers.filter(m => participantIds.has(String(m.userId)));

      if (splitMode === 'custom') {
        const customSplits = selectedMembers.map(m => {
          if (!m.userId) throw new Error(`Member "${m.firstName}" is missing a userId`);
          return { userId: m.userId, amount: parseInt(customAmounts[String(m.userId)]) };
        });
        const payload = { groupId: String(groupId), category: title.trim(), amount: parsedAmount, createdBy: payerId, customSplits, season, seasonYear };
        if (isEditMode) {
          await financialTransactionAPI.updateExpense(expense.id, payload, expense.creditTransactions.map(ct => ct.id));
        } else {
          await financialTransactionAPI.createExpense(payload);
        }
      } else {
        const participantIdsArr = selectedMembers.map(m => {
          if (!m.userId) throw new Error(`Member "${m.firstName}" is missing a userId`);
          return m.userId;
        });
        const payload = { groupId: String(groupId), category: title.trim(), amount: parsedAmount, payers: participantIdsArr, createdBy: payerId, season, seasonYear };
        if (isEditMode) {
          await financialTransactionAPI.updateExpense(expense.id, payload, expense.creditTransactions.map(ct => ct.id));
        } else {
          await financialTransactionAPI.createExpense(payload);
        }
      }

      toast.success(isEditMode ? 'Expense updated!' : 'Expense added!');
      onSaved();
    } catch (err) {
      toast.error(err.message || (isEditMode ? 'Failed to update expense' : 'Failed to add expense'));
    } finally {
      setSaving(false);
    }
  };

  const subtitles = {
    1: 'Enter the expense details',
    2: 'Select who paid for this',
    3: 'Choose who splits the cost',
  };

  const inputCls = `w-full px-3 py-2.5 rounded-xl border ${theme.border} ${theme.input} text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 transition`;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
      <div className={`${theme.card} w-full sm:max-w-md sm:rounded-2xl rounded-t-2xl shadow-2xl flex flex-col`}
           style={{ maxHeight: '92vh' }}>

        {/* ── Header ─────────────────────────────────────────────────── */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-t-2xl px-5 pt-5 pb-5 flex-shrink-0">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-white/20 rounded-xl flex items-center justify-center">
                <Receipt className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white leading-tight">{isEditMode ? 'Edit Expense' : 'Add Expense'}</h3>
                <p className="text-blue-100 text-xs">{subtitles[step]}</p>
              </div>
            </div>
            <button onClick={onClose}
              className="p-1.5 rounded-full bg-white/20 hover:bg-white/30 transition-colors mt-0.5">
              <X className="w-4 h-4 text-white" />
            </button>
          </div>

          {/* Step indicator */}
          <div className="flex items-center">
            {STEPS.map((s, i) => {
              const done    = step > s.number;
              const current = step === s.number;
              return (
                <React.Fragment key={s.number}>
                  <div className="flex flex-col items-center gap-1">
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-all ${
                      done    ? 'bg-white text-blue-600' :
                      current ? 'bg-white text-blue-600 ring-2 ring-white/40' :
                                'bg-white/20 text-white/60'
                    }`}>
                      {done ? <Check className="w-3.5 h-3.5" /> : s.number}
                    </div>
                    <span className={`text-xs font-medium ${current || done ? 'text-white' : 'text-white/50'}`}>
                      {s.label}
                    </span>
                  </div>
                  {i < STEPS.length - 1 && (
                    <div className={`flex-1 h-0.5 mb-4 mx-1 rounded-full transition-all ${
                      step > s.number ? 'bg-white' : 'bg-white/25'
                    }`} />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>

        {/* ── Body ───────────────────────────────────────────────────── */}
        <div className="flex-1 overflow-y-auto px-5 py-5 space-y-4">

          {/* ─ Step 1: Details ─ */}
          {step === 1 && (
            <>
              <div className={`rounded-2xl border-2 ${parsedAmount > 0 ? 'border-blue-500' : theme.border} px-4 py-3 flex items-center gap-3 ${theme.backgroundSecondary}`}>
                <span className={`text-2xl font-light ${parsedAmount > 0 ? theme.link : theme.textMuted}`}>$</span>
                <input
                  type="text"
                  inputMode="numeric"
                  value={amount}
                  onChange={e => {
                    const val = e.target.value;
                    if (val === '' || /^\d+$/.test(val)) setAmount(val);
                  }}
                  placeholder="0"
                  autoFocus
                  className={`flex-1 bg-transparent text-3xl font-bold outline-none min-w-0 ${theme.text}`}
                />
                {parsedAmount > 0 && (
                  <span className={`text-xs font-semibold ${theme.link} ${theme.backgroundSecondary} px-2 py-0.5 rounded-full border ${theme.border}`}>
                    USD
                  </span>
                )}
              </div>

              <div>
                <label className={`block text-xs font-semibold uppercase tracking-wide ${theme.textSecondary} mb-1.5`}>
                  What's it for? *
                </label>
                <input
                  type="text"
                  value={title}
                  onChange={e => setTitle(e.target.value)}
                  placeholder="e.g. Court booking, Team dinner…"
                  className={inputCls}
                />
              </div>

              <div>
                <label className={`block text-xs font-semibold uppercase tracking-wide ${theme.textSecondary} mb-1.5`}>
                  Due Date <span className="normal-case font-normal">(optional)</span>
                </label>
                <input
                  type="date"
                  value={dueDate}
                  onChange={e => setDueDate(e.target.value)}
                  className={inputCls}
                />
              </div>

              <div>
                <label className={`block text-xs font-semibold uppercase tracking-wide ${theme.textSecondary} mb-1.5`}>
                  Season
                </label>
                <div className="flex gap-1.5 overflow-x-auto pb-1 -mx-1 px-1">
                  {SEASONS.map(s => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => setSeason(s)}
                      className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${
                        season === s
                          ? 'bg-blue-500 text-white'
                          : `${theme.backgroundSecondary} border ${theme.border} ${theme.textSecondary}`
                      }`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className={`block text-xs font-semibold uppercase tracking-wide ${theme.textSecondary} mb-1.5`}>
                  Year
                </label>
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => setSeasonYear(y => y - 1)}
                    className={`w-8 h-8 rounded-full flex items-center justify-center ${theme.backgroundSecondary} border ${theme.border} ${theme.text} font-bold`}
                  >
                    ‹
                  </button>
                  <span className={`flex-1 text-center text-sm font-semibold ${theme.text}`}>{seasonYear}</span>
                  <button
                    type="button"
                    onClick={() => setSeasonYear(y => y + 1)}
                    className={`w-8 h-8 rounded-full flex items-center justify-center ${theme.backgroundSecondary} border ${theme.border} ${theme.text} font-bold`}
                  >
                    ›
                  </button>
                </div>
              </div>
            </>
          )}

          {/* ─ Step 2: Who Paid ─ */}
          {step === 2 && (
            <>
              <p className={`text-sm ${theme.textSecondary}`}>
                Select the person who paid <span className={`font-semibold ${theme.text}`}>${parsedAmount}</span> upfront.
              </p>
              <div className="space-y-2">
                {allMembers.map((m, idx) => {
                  const id       = String(m.userId);
                  const selected = payerId === id;
                  const isMe     = id === String(currentUserId);
                  return (
                    <button key={id} type="button" onClick={() => handlePayerSelect(id)}
                      className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl border-2 transition-all text-left ${
                        selected ? `border-blue-500 ${theme.backgroundSecondary}` : `${theme.border} ${theme.card} ${theme.cardHover}`
                      }`}>
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold text-white flex-shrink-0 ${avatarColor(id, idx)}`}>
                        {getInitials(m)}
                      </div>
                      <p className={`flex-1 text-sm font-semibold ${theme.text} truncate`}>
                        {getMemberName(m)}{isMe ? ' (You)' : ''}
                      </p>
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-all ${
                        selected ? 'border-blue-500 bg-blue-500' : theme.border
                      }`}>
                        {selected && <Check className="w-3 h-3 text-white" />}
                      </div>
                    </button>
                  );
                })}
              </div>
            </>
          )}

          {/* ─ Step 3: Split With ─ */}
          {step === 3 && (
            <>
              {/* Split mode toggle */}
              <div className={`flex rounded-xl border ${theme.border} p-1 ${theme.backgroundSecondary}`}>
                {['equal', 'custom'].map(mode => (
                  <button
                    key={mode}
                    type="button"
                    onClick={() => setSplitMode(mode)}
                    className={`flex-1 py-1.5 text-sm font-semibold rounded-lg transition-all capitalize ${
                      splitMode === mode
                        ? `${theme.card} ${theme.text} shadow-sm`
                        : theme.textSecondary
                    }`}
                  >
                    {mode === 'equal' ? 'Equal Split' : 'Custom Split'}
                  </button>
                ))}
              </div>

              {/* Participants header */}
              <div className="flex items-center justify-between">
                <p className={`text-sm ${theme.textSecondary}`}>Select who shares this expense.</p>
                <div className="flex gap-3 text-xs">
                  <button type="button"
                    onClick={() => setParticipantIds(new Set(participantPool.map(m => String(m.userId))))}
                    className={`${theme.link} font-semibold`}>All</button>
                  <button type="button"
                    onClick={() => setParticipantIds(new Set())}
                    className={theme.textSecondary}>None</button>
                </div>
              </div>

              {participantPool.length === 0 ? (
                <p className={`text-sm ${theme.textSecondary} py-4 text-center`}>No other members available.</p>
              ) : (
                <div className="space-y-2">
                  {participantPool.map((m, idx) => {
                    const id       = String(m.userId);
                    const selected = participantIds.has(id);
                    const isMe     = id === String(currentUserId);
                    return (
                      <div key={id}
                        className={`flex items-center gap-3 px-4 py-3 rounded-xl border-2 transition-all ${
                          selected ? `border-blue-500 ${theme.backgroundSecondary}` : `${theme.border} ${theme.card}`
                        }`}>
                        {/* Checkbox tap area */}
                        <button type="button" onClick={() => toggleParticipant(id)}
                          className="flex items-center gap-3 flex-1 min-w-0 text-left">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold text-white flex-shrink-0 ${avatarColor(id, idx)}`}>
                            {getInitials(m)}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <p className={`text-sm font-semibold ${theme.text} truncate`}>
                                {getMemberName(m)}{isMe ? ' (You)' : ''}
                              </p>
                              {id === payerId && (
                                <span className="flex-shrink-0 text-xs font-semibold text-emerald-600 bg-emerald-100 dark:bg-emerald-900/40 dark:text-emerald-400 px-1.5 py-0.5 rounded-full">
                                  Paid
                                </span>
                              )}
                            </div>
                            {selected && splitMode === 'equal' && equalPerPerson > 0 && (
                              <p className={`text-xs ${theme.link} font-medium`}>${equalPerPerson} share</p>
                            )}
                          </div>
                          <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 transition-all ${
                            selected ? 'border-blue-500 bg-blue-500' : theme.border
                          }`}>
                            {selected && <Check className="w-3 h-3 text-white" />}
                          </div>
                        </button>

                        {/* Custom amount input */}
                        {selected && splitMode === 'custom' && (
                          <div className={`flex items-center gap-1 border ${theme.border} rounded-lg px-2 py-1 w-24 flex-shrink-0 ${theme.backgroundSecondary}`}>
                            <span className={`text-sm ${theme.textSecondary}`}>$</span>
                            <input
                              type="text"
                              inputMode="numeric"
                              value={customAmounts[id] ?? ''}
                              onChange={e => {
                                const val = e.target.value;
                                if (val === '' || /^\d+$/.test(val)) {
                                  setCustomAmounts(prev => ({ ...prev, [id]: val }));
                                }
                              }}
                              className={`w-full bg-transparent text-sm font-bold outline-none ${theme.text}`}
                              placeholder="0"
                            />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Balance indicator */}
              {participantCount > 0 && parsedAmount > 0 && (
                <div className={`rounded-xl border px-4 py-3 ${
                  splitMode === 'equal' || isBalanced
                    ? `${theme.backgroundSecondary} ${theme.border}`
                    : 'bg-red-50 border-red-200 dark:bg-red-900/20 dark:border-red-800'
                }`}>
                  {splitMode === 'equal' ? (
                    <div className="flex items-center justify-between">
                      <span className={`text-sm ${theme.textSecondary}`}>
                        ${parsedAmount} ÷ {participantCount} {participantCount === 1 ? 'person' : 'people'}
                      </span>
                      <span className={`text-lg font-bold ${theme.link}`}>
                        ${equalPerPerson}
                        <span className={`text-xs font-normal ${theme.textSecondary} ml-1`}>each</span>
                      </span>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className={`text-sm ${theme.textSecondary}`}>Total expense</span>
                        <span className={`text-sm font-semibold ${theme.text}`}>${parsedAmount}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className={`text-sm ${theme.textSecondary}`}>Allocated</span>
                        <span className={`text-sm font-semibold ${isBalanced ? 'text-green-500' : 'text-red-500'}`}>
                          ${customTotal}
                        </span>
                      </div>
                      <div className={`h-px ${theme.border}`} />
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-1.5">
                          {isBalanced
                            ? <CheckCircle2 className="w-4 h-4 text-green-500" />
                            : <AlertCircle className="w-4 h-4 text-red-500" />
                          }
                          <span className={`text-sm font-semibold ${isBalanced ? 'text-green-500' : 'text-red-500'}`}>
                            {isBalanced ? 'Balanced!' : remaining > 0 ? `$${remaining} remaining` : `$${Math.abs(remaining)} over`}
                          </span>
                        </div>
                        {!isBalanced && (
                          <button type="button"
                            onClick={() => {
                              // Auto-distribute remaining to first selected participant
                              const ids = [...participantIds];
                              if (ids.length === 0) return;
                              const firstId = ids[0];
                              const cur = parseInt(customAmounts[firstId]) || 0;
                              setCustomAmounts(prev => ({ ...prev, [firstId]: String(cur + remaining) }));
                            }}
                            className={`text-xs ${theme.link} font-semibold`}>
                            Auto-fix
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {/* ── Footer ─────────────────────────────────────────────────── */}
        <div className={`px-5 pb-5 pt-3 border-t ${theme.border} flex gap-3 flex-shrink-0`}>
          {step > 1 ? (
            <button type="button" onClick={handleBack}
              className={`flex items-center gap-1.5 ${btn.base} ${btn.md} ${theme.buttonSecondary}`}>
              <ChevronLeft className="w-4 h-4" /> Back
            </button>
          ) : (
            <button type="button" onClick={onClose}
              className={`flex-1 ${btn.base} ${btn.md} ${theme.buttonSecondary}`}>
              Cancel
            </button>
          )}

          {step < 3 ? (
            <button type="button" onClick={handleNext}
              disabled={step === 1 ? !step1Valid : !step2Valid}
              className={`flex-1 flex items-center justify-center gap-1.5 ${btn.base} ${btn.md} ${theme.buttonPrimary}`}>
              Next <ChevronRight className="w-4 h-4" />
            </button>
          ) : (
            <button type="button" onClick={handleSubmit}
              disabled={saving || !step3Valid}
              className={`flex-1 flex items-center justify-center gap-2 ${btn.base} ${btn.md} ${theme.buttonPrimary}`}>
              {saving && <Loader className="w-4 h-4 animate-spin" />}
              {saving ? 'Saving…' : isEditMode ? 'Save Changes' : 'Add Expense'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default AddExpenseComponent;
